import axios from 'axios';

// API base for axios (with /api). Set REACT_APP_API_URL in non-local environments (e.g. https://api.example.com/api).
// Fallback rules:
// - Dev: hit the local backend on :5000.
// - Prod: fall back to the current page origin so images/sockets at least target the served
//   domain instead of localhost. If the prod API lives on a different domain, REACT_APP_API_URL
//   must be set at build time.
function resolveApiUrl() {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) return envUrl;
  if (process.env.NODE_ENV === 'production') {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[api] REACT_APP_API_URL is not set; falling back to window.location.origin. ' +
          'Set it at build time if the API is on a different domain.'
      );
      return `${window.location.origin}/api`;
    }
    return '/api';
  }
  return 'http://localhost:5000/api';
}

const API_URL = resolveApiUrl();
// Origin for building asset URLs (uploads, images). Same server, no /api path.
export const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_URL,
  // Send the httpOnly auth cookie (and CSRF cookie) on every request. The session
  // JWT is no longer in JS/localStorage — it lives in an httpOnly cookie the
  // browser attaches automatically.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Read a non-httpOnly cookie value (used for the CSRF token).
function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : '';
}

// CSRF double-submit: echo the csrf_token cookie in the X-CSRF-Token header on
// state-changing requests so the backend's check passes. Safe methods are exempt.
// The customer-checkout flow still sets its own Authorization header per request
// (createOrder below); that is preserved and the CSRF header is added alongside.
api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (!['get', 'head', 'options'].includes(method)) {
      const csrf = readCookie('csrf_token');
      if (csrf) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized) – redirect to login with return URL.
// Callers can set `config.skipAuthRedirect = true` to opt out (used by the
// customer order POST so a 401 from a stale checkout token does not boot a
// logged-in manager / an unauthenticated diner to /login).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      // The auth JWT is an httpOnly cookie the server clears on logout/expiry;
      // here we only drop the cached user profile and bounce to login.
      localStorage.removeItem('user');
      const returnPath = window.location.pathname + window.location.search;
      const redirect = returnPath && returnPath !== '/' && returnPath !== '/login'
        ? `?redirect=${encodeURIComponent(returnPath)}`
        : '';
      window.location.href = `/login${redirect}`;
    }
    return Promise.reject(error);
  }
);

// Templates
export const getTemplates = () => api.get('/templates');
export const getTemplate = (id) => api.get(`/templates/${id}`);

// Currencies
export const getCurrencies = () => api.get('/currencies');

// Restaurants
export const getRestaurants = () => api.get('/restaurants');
export const getRestaurant = (id) => api.get(`/restaurants/${id}`);
export const createRestaurant = (data) => api.post('/restaurants', data);
export const updateRestaurant = (id, data) => api.put(`/restaurants/${id}`, data);
export const updateSectionOrder = (id, sectionOrder) => api.put(`/restaurants/${id}/section-order`, { sectionOrder });
export const deleteRestaurant = (id) => api.delete(`/restaurants/${id}`);
export const applyTemplate = (restaurantId, templateId) =>
  api.post(`/restaurants/${restaurantId}/apply-template`, { templateId });

// Restaurant info (customer-facing page + manager form)
export const getRestaurantInfo = (restaurantId, lang) =>
  api.get(`/restaurants/${restaurantId}/info`, lang ? { params: { lang } } : {});

// Menu theme (customer menu page + manager theme settings)
export const getRestaurantTheme = (restaurantId) => api.get(`/restaurants/${restaurantId}/theme`);
export const updateRestaurantTheme = (restaurantId, themeId, customConfig = null) =>
  api.put(`/restaurants/${restaurantId}/theme`, { themeId, customConfig });
export const getAvailableThemes = () => api.get('/restaurants/themes/available');
export const updateRestaurantInfo = (restaurantId, data) => api.put(`/restaurants/${restaurantId}/info`, data);
export const uploadRestaurantPhoto = (restaurantId, formData) =>
  api.post(`/restaurants/${restaurantId}/info/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Menu Items (optional lang: 'en'|'es'|'ar' for translated name/description)
export const getMenuItems = (restaurantId, lang) => {
  const params = lang ? { params: { lang } } : {};
  return api.get(`/restaurants/${restaurantId}/menu`, params);
};
/** Get all menu items including unavailable (for admin/manager panel) */
export const getMenuItemsAll = (restaurantId) => api.get(`/restaurants/${restaurantId}/menu/all`);
export const addMenuItem = (restaurantId, data) => api.post(`/restaurants/${restaurantId}/menu/base64`, data);
export const updateMenuItem = (id, data) => api.put(`/menu-items/${id}`, data);
export const updateMenuItemAvailability = (id, is_available) =>
  api.patch(`/menu-items/${id}/availability`, { is_available });
/** Update availability with optional out-of-stock reason (i18n) */
export const updateItemAvailability = (itemId, data) =>
  api.post(`/menu-items/${itemId}/availability`, data);
export const deleteMenuItem = (id) => api.delete(`/menu-items/${id}`);

// Menu item schedules (time-based availability)
export const getItemSchedules = (itemId) =>
  api.get(`/menu-items/${itemId}/schedules`);
export const createItemSchedule = (itemId, data) =>
  api.post(`/menu-items/${itemId}/schedules`, data);
export const updateItemSchedule = (scheduleId, data) =>
  api.put(`/menu-item-schedules/${scheduleId}`, data);
export const deleteItemSchedule = (scheduleId) =>
  api.delete(`/menu-item-schedules/${scheduleId}`);

// Schedule templates
export const getScheduleTemplates = (restaurantId) =>
  api.get(`/restaurants/${restaurantId}/schedule-templates`);
export const createScheduleTemplate = (restaurantId, data) =>
  api.post(`/restaurants/${restaurantId}/schedule-templates`, data);
export const applyScheduleTemplate = (menuItemId, templateId) =>
  api.post(`/menu-items/${menuItemId}/apply-template`, { templateId });

// Menu item sizes (Small / Medium / Large with per-size pricing)
export const getMenuItemSizes = (menuItemId) =>
  api.get(`/menu-items/${menuItemId}/sizes`);
export const createMenuItemSize = (menuItemId, data) =>
  api.post(`/menu-items/${menuItemId}/sizes`, data);
export const updateMenuItemSize = (id, data) =>
  api.put(`/menu-item-sizes/${id}`, data);
export const deleteMenuItemSize = (id) =>
  api.delete(`/menu-item-sizes/${id}`);

// Menu item addons
export const getMenuItemAddons = (menuItemId) => api.get(`/menu-items/${menuItemId}/addons`);
export const createAddon = (menuItemId, data) => api.post(`/menu-items/${menuItemId}/addons`, data);
export const updateAddon = (addonId, data) => api.put(`/addons/${addonId}`, data);
export const deleteAddon = (addonId) => api.delete(`/addons/${addonId}`);
export const updateAddonOption = (optionId, data) => api.put(`/addon-options/${optionId}`, data);
export const deleteAddonOption = (optionId) => api.delete(`/addon-options/${optionId}`);

// Orders (optional: page, limit, status, statusIn, dateRange, search)
export const getOrders = (restaurantId, { page = 1, limit = 20, status, statusIn, dateRange, search } = {}) =>
  api.get(`/restaurants/${restaurantId}/orders`, { params: { page, limit, status, statusIn, dateRange, search } });
export const createOrder = (restaurantId, data, { checkoutToken } = {}) => {
  // skipAuthRedirect: a 401 from a stale checkout token must NOT boot the
  // diner (or a logged-in manager testing the flow) to /login; the Menu page
  // catches the 401 and reopens the OTP dialog instead.
  const config = { skipAuthRedirect: true };
  if (checkoutToken) {
    config.headers = { Authorization: `Bearer ${checkoutToken}` };
  }
  return api.post(`/restaurants/${restaurantId}/orders`, data, config);
};
export const updateOrderStatus = (orderId, status) => api.patch(`/orders/${orderId}/status`, { status });

// Cloud POS — table tabs, POS orders, payments, receipts (all authenticated +
// tenant-scoped by the backend). Mounted under /api/restaurants/:id/pos.
export const openTab = (restaurantId, tableNumber) =>
  api.post(`/restaurants/${restaurantId}/pos/tabs`, { table_number: tableNumber });
export const listOpenTabs = (restaurantId) =>
  api.get(`/restaurants/${restaurantId}/pos/tabs`);
export const getTab = (restaurantId, sessionId) =>
  api.get(`/restaurants/${restaurantId}/pos/tabs/${sessionId}`);
export const createPosOrder = (restaurantId, data) =>
  api.post(`/restaurants/${restaurantId}/pos/orders`, data);
export const addOrderToTab = (restaurantId, sessionId, data) =>
  api.post(`/restaurants/${restaurantId}/pos/tabs/${sessionId}/orders`, data);
export const createPosPaymentIntent = (restaurantId, sessionId, amount) =>
  api.post(`/restaurants/${restaurantId}/pos/tabs/${sessionId}/payment-intent`, amount != null ? { amount } : {});
export const recordCashPayment = (restaurantId, sessionId, data) =>
  api.post(`/restaurants/${restaurantId}/pos/tabs/${sessionId}/cash-payment`, data);
export const getPosPayment = (restaurantId, paymentId) =>
  api.get(`/restaurants/${restaurantId}/pos/payments/${paymentId}`);
export const closeTab = (restaurantId, sessionId) =>
  api.post(`/restaurants/${restaurantId}/pos/tabs/${sessionId}/close`);
export const getPosReceipt = (restaurantId, sessionId) =>
  api.get(`/restaurants/${restaurantId}/pos/tabs/${sessionId}/receipt`);

// Customer phone-OTP at checkout (only used when the restaurant has
// require_customer_otp = 1). `send` always returns 200; `verify` returns
// { checkout_token } which the caller passes to createOrder.
export const sendCustomerOtp = (restaurantId, phone) =>
  api.post('/customer-otp/send', { restaurantId, phone });
export const verifyCustomerOtp = (restaurantId, phone, code) =>
  api.post('/customer-otp/verify', { restaurantId, phone, code });

// Kitchen (KDS)
export const getKitchenStations = (restaurantId) =>
  api.get('/kitchen/stations', { params: { restaurantId } });
export const getKitchenOrders = (restaurantId, params = {}) =>
  api.get('/kitchen/orders', { params: { restaurantId, ...params } });
export const getKitchenOrderDetails = (orderId) => api.get(`/kitchen/orders/${orderId}`);
export const getKitchenQueue = (restaurantId) =>
  api.get('/kitchen/queue', { params: { restaurantId } });
export const startPreparingItem = (orderId, itemId, stationId) =>
  api.post(`/kitchen/orders/${orderId}/items/${itemId}/start`, { stationId });
export const completeItem = (orderId, itemId, stationId) =>
  api.post(`/kitchen/orders/${orderId}/items/${itemId}/complete`, { stationId });
export const resetItem = (orderId, itemId, stationId) =>
  api.post(`/kitchen/orders/${orderId}/items/${itemId}/reset`, { stationId });
export const bumpOrder = (orderId) => api.post(`/kitchen/orders/${orderId}/bump`);
export const recallOrder = (orderId) => api.post(`/kitchen/orders/${orderId}/recall`);
export const getKitchenDisplay = (displayId) => api.get(`/kitchen/displays/${displayId}`);
export const updateKitchenDisplay = (displayId, data) => api.put(`/kitchen/displays/${displayId}`, data);
export const kitchenDisplayHeartbeat = (displayId) => api.post(`/kitchen/displays/${displayId}/heartbeat`);
export const getKitchenAnalytics = (restaurantId, startDate, endDate) =>
  api.get('/kitchen/analytics', { params: { restaurantId, startDate, endDate } });

// Service Requests (Call Waiter / Request Bill / Need Help)
export const createServiceRequest = (data) => api.post('/service-requests', data);
export const getActiveServiceRequests = (restaurantId, status) =>
  api.get(`/service-requests/restaurant/${restaurantId}`, status ? { params: { status } } : {});
export const acknowledgeServiceRequest = (requestId) =>
  api.post(`/service-requests/${requestId}/acknowledge`);
export const completeServiceRequest = (requestId) =>
  api.post(`/service-requests/${requestId}/complete`);
export const cancelServiceRequest = (requestId) =>
  api.post(`/service-requests/${requestId}/cancel`);
export const getServiceRequestAnalytics = (restaurantId, startDate, endDate) =>
  api.get(`/service-requests/restaurant/${restaurantId}/analytics`, {
    params: startDate && endDate ? { startDate, endDate } : {}
  });

// QR Codes
export const generateQRCode = (restaurantId, tableNumber) =>
  api.post(`/restaurants/${restaurantId}/qr-codes`, { table_number: tableNumber });
export const generateCarQRCode = (restaurantId) =>
  api.post(`/restaurants/${restaurantId}/qr-codes/car`);
export const getQRCodes = (restaurantId) => api.get(`/restaurants/${restaurantId}/qr-codes`);
export const updateQRCode = (restaurantId, id, data) =>
  api.put(`/restaurants/${restaurantId}/qr-codes/${id}`, data);
export const deleteQRCode = (restaurantId, id) =>
  api.delete(`/restaurants/${restaurantId}/qr-codes/${id}`);

// Sections (rename = bulk update menu_items section; delete = move items to Other)
export const renameSection = (restaurantId, oldName, newName) =>
  api.patch(`/restaurants/${restaurantId}/sections/rename`, { oldName, newName });
export const deleteSection = (restaurantId, sectionName) =>
  api.delete(`/restaurants/${restaurantId}/sections`, { data: { sectionName } });

// Section (category) name translations — keyed by (restaurant, section_name, language)
export const getSectionTranslations = (restaurantId) =>
  api.get(`/restaurants/${restaurantId}/section-translations`);
export const saveSectionTranslations = (restaurantId, sectionName, translations) =>
  api.post(`/restaurants/${restaurantId}/section-translations`, { sectionName, translations });

// Authentication
export const login = (email, password) => api.post('/auth/login', { email, password });
export const loginAsDemoManager = () => api.post('/auth/demo-login');
// OAuth: exchange a provider token for a Mesoon session (same { token, user, restaurants } shape as login).
export const googleAuth = (credential) => api.post('/auth/google', { credential });
export const appleAuth = (payload) => api.post('/auth/apple', payload);
export const register = (data) => api.post('/auth/register', data);
export const verifyEmail = (token) => api.post('/auth/verify-email', { token });
export const resendVerification = (email) => api.post('/auth/resend-verification', { email });
export const logout = () => api.post('/auth/logout');
export const requestPasswordReset = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, newPassword });
export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword });
// skipAuthRedirect: the on-mount session check must not bounce a visitor sitting
// on a public page to /login; AuthContext clears local state on failure instead.
export const getCurrentUser = () => api.get('/auth/me', { skipAuthRedirect: true });
export const getUsers = () => api.get('/auth/users');
export const updateUser = (id, data) => api.put(`/auth/users/${id}`, data);
export const adminUpdateUserPassword = (id, newPassword) =>
  api.put(`/auth/users/${id}/password`, { newPassword });
export const deleteUser = (id) => api.delete(`/auth/users/${id}`);

// Translation Services
export const getSupportedLanguages = () => api.get('/translate/languages');
export const translateMenuItem = (data) => api.post('/translate/menu-item', data);
export const autoTranslateItem = (menuItemId, targetLangs, sourceLang = 'en') => 
  api.post(`/menu-items/${menuItemId}/auto-translate`, { targetLangs, sourceLang });
export const translateAllMenu = (restaurantId, sourceLang, targetLangs) =>
  api.post(`/restaurants/${restaurantId}/translate-all`, { sourceLang, targetLangs });
export const getMenuItemTranslations = (menuItemId) => 
  api.get(`/menu-items/${menuItemId}/translations`);
export const updateMenuItemTranslation = (menuItemId, langCode, data) =>
  api.put(`/menu-items/${menuItemId}/translations/${langCode}`, data);
export const saveMenuItemTranslations = (menuItemId, translations) =>
  api.post(`/menu-items/${menuItemId}/translations`, { translations });

// Analytics (manager dashboard)
export const getDashboardOverview = async (restaurantId, period = 'week') => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/overview`,
    { params: { period } }
  );
  return data;
};
export const getSalesTrend = async (restaurantId, period = 'week') => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/sales-trend`,
    { params: { period } }
  );
  return data;
};
export const getTopItems = async (restaurantId, period = 'week', limit = 10) => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/top-items`,
    { params: { period, limit } }
  );
  return data;
};
export const getPeakHours = async (restaurantId, period = 'week') => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/peak-hours`,
    { params: { period } }
  );
  return data;
};
export const getCategoryPerformance = async (restaurantId, period = 'week') => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/categories`,
    { params: { period } }
  );
  return data;
};
export const getOrderStatusBreakdown = async (restaurantId, period = 'week') => {
  const { data } = await api.get(
    `/analytics/restaurant/${restaurantId}/order-status`,
    { params: { period } }
  );
  return data;
};

/**
 * Admin: platform-wide analytics across all restaurants
 */
export const getAllRestaurantsAnalytics = (period = 'week') =>
  api.get('/analytics/admin/all-restaurants', { params: { period } })
    .then((r) => r.data);

/**
 * Demo dashboard (public, read-only) — powers the manager-dashboard preview
 * reached from the customer demo flow. Aggregates only is_demo orders.
 */
export const getDemoDashboard = (restaurantId) =>
  api.get(`/restaurants/${restaurantId}/demo-dashboard`).then((r) => r.data);

// Subscriptions
export const listSubscriptionPlans = () => api.get('/subscriptions/plans');
export const getMySubscription = () => api.get('/subscriptions/me');

// Admin-only subscription management
export const adminListSubscriptions = () => api.get('/subscriptions/admin/all');
export const adminMarkSubscriptionPaid = (restaurantId, interval) =>
  api.post(`/subscriptions/admin/${restaurantId}/mark-paid`, { interval });
export const adminCancelSubscription = (restaurantId) =>
  api.post(`/subscriptions/admin/${restaurantId}/cancel`);
export const adminExtendTrial = (restaurantId, days) =>
  api.post(`/subscriptions/admin/${restaurantId}/extend-trial`, { days });

export default api;
